import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { UiContext } from 'shared/lib/contexts/uiContext';
import Modal, { ModalProps } from 'shared/lib/components/modals/modal';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import CustomTabs from 'shared/lib/components/controls/tabs/customTabs';
import CustomTab from 'shared/lib/components/controls/tabs/customTab';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import { emptySelectItemId } from 'shared/lib/utils/globalUtils';
import PlacementSelection from 'widgets/selectPlacement/placementSelection';
import { MergeInitialPlacement } from 'entities/types';

export type FolderEditParams = {
  name: string;
  sectionId?: string;
  folderId?: string;
  parent_id?: string | null;
};

export default observer(function EditFolderModal({
  mergePlacementParams,
  handleClose,
  isOpen,
  name,
  onSubmit,
  onDelete,
  headerText,
  submitText,
  isOnlyName = false,
  afterFolderCreate,
}: {
  mergePlacementParams?: MergeInitialPlacement;
  name: string;
  onSubmit: (params: FolderEditParams) => Promise<any>;
  onDelete?: () => void;
  headerText?: string;
  submitText?: string;
  isOnlyName?: boolean;
  afterFolderCreate?: () => void;
} & ModalProps): JSX.Element {
  const { t } = useTranslation();

  headerText = headerText || t('common.settings');
  submitText = submitText || t('common.save');

  const theme = useTheme();
  const {
    getFontSize,
    spacings: { sp },
  } = useContext(UiContext);
  const f18 = getFontSize('f18');
  const [isLoading, setIsLoading] = useState(false);

  const initialSectionId = mergePlacementParams?.initialSectionId || '';
  const initialFolderId = mergePlacementParams?.initialFolderId || '';

  const [selectedFolderId, setSelectedFolderId] =
    useState<string>(initialFolderId);
  const [selectedSectionId, setSelectedSectionId] =
    useState<string>(initialSectionId);

  const isDisabled = selectedSectionId === emptySelectItemId;

  const validationSchema = yup.object({
    folderName: yup.string().required(t('errors.validationSchema.required')),
  });

  const [formikSubmited, setFormikSubmited] = useState(false);
  const formik = useFormik({
    initialValues: {
      folderName: name,
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async (params) => {
      setIsLoading(true);

      try {
        await onSubmit({
          name: params.folderName,
          sectionId: selectedSectionId,
          parent_id: selectedFolderId || null,
        });
        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setIsLoading(false);
      }
    },
  });

  const finalIsDisabled = useMemo(() => {
    const changes =
      formik.values.folderName !== name ||
      selectedSectionId !== initialSectionId ||
      selectedFolderId !== initialFolderId;
    return isOnlyName ? false : !(!isLoading && changes && !isDisabled);
  }, [
    isOnlyName,
    isLoading,
    isDisabled,
    formik.values.folderName,
    name,
    selectedSectionId,
    initialSectionId,
    selectedFolderId,
    initialFolderId,
  ]);

  const handleLoadingChange = (isLoading: boolean) => {
    setIsLoading(isLoading);
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <Typography variant="f24">{headerText}</Typography>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(sp.s50),
        }}
        onSubmit={formik.handleSubmit}
      >
        <CustomTextField
          wrapperSxProps={{ width: 1 }}
          id="folderName"
          name="folderName"
          label={t('formFields.folderName')}
          value={formik.values.folderName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.folderName && !!formik.errors.folderName}
          helperText={formik.touched.folderName && formik.errors.folderName}
          autoFocus={true}
          isClearIcon={true}
          labelTypographyVariantSize="f14"
          labelSxProps={{
            color: 'text.secondary',
          }}
        />

        {!isOnlyName && (
          <Paper
            elevation={4}
            sx={{
              display: 'flex',
              position: 'relative',
              flexDirection: 'column',
              overflow: 'hidden',
              width: 1,
              borderRadius: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                px: sp.s60,
                py: sp.s30,
                borderBottom: `1px solid ${theme.palette.custom.separatorLine}`,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: `${f18} !important`,
                  color: 'text.primary',
                }}
              >
                Перенос папки
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: sp.s40,
                p: sp.s60,
              }}
            >
              <PlacementSelection
                initialSectionId={selectedSectionId}
                initialFolderId={selectedFolderId || ''}
                afterFolderCreate={afterFolderCreate}
                onChangeFolder={(folderId) =>
                  setSelectedFolderId(folderId || '')
                }
                onChangeSection={(sectionId) => setSelectedSectionId(sectionId)}
                filterFoldersById={mergePlacementParams?.filterFoldersById}
                labelProps={{
                  textFolder: t('common.folderSelection'),
                  textSection: t('section.section'),
                  sx: {
                    color: 'text.secondary',
                  },
                }}
                onLoadingChange={handleLoadingChange}
              />
            </Box>
          </Paper>
        )}

        <ButtonsContainer
          variant="vertical"
          sx={{ width: 1, mt: isOnlyName ? 0 : sp.s40 }}
        >
          <ButtonsContainer>
            <SecondaryButton variant="outlined" onClick={handleClose}>
              {t('common.cancel')}
            </SecondaryButton>
            <PrimaryButton
              loading={isLoading}
              type="submit"
              disabled={finalIsDisabled}
              onClick={() => {
                setFormikSubmited(true);
              }}
            >
              {submitText}
            </PrimaryButton>
          </ButtonsContainer>

          {onDelete ? (
            <PrimaryButton onClick={() => onDelete()} color="error">
              {t('common.delete')}
            </PrimaryButton>
          ) : (
            ''
          )}
        </ButtonsContainer>
      </form>
    </Modal>
  );
});
